import { ChevronLeft, ChevronRight, Loading2 } from 'assets/icons';
import Button from 'components/button';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import { addDays, clockLabel, fromIsoDay, isoDay } from 'helper/timeLabels';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import {
  appointmentDays,
  appointmentLook,
  gridHours,
  isInvitation,
  mondayOf,
  responseOf,
  sameDay,
  spansDays,
  weekRange,
} from 'pages/planning/planningItems';
import { useEffect, useMemo, useRef, useState } from 'react';
import crmApi from 'server/crm/crmApi';
import WeekGrid from './WeekGrid';

/**
 * The week of the signed-in person beside an appointment being written, as in
 * HubSpot: their appointments and their tasks at a time, the new one dashed
 * (`start`, `minutes`, `title`). A click on a free slot moves it there, a drag
 * also sets its length: onPick({ start, minutes }), `minutes` null on a click.
 * Monday to Friday, the weekend too when the appointment falls on it. The week
 * follows `start`; the arrows look at another one. `onLoaded` gets each week
 * read (calendarSynced tells whether Google Calendar is synced).
 */
const WeekPicker = ({ start, minutes, title, onPick, onLoaded }) => {
  const { text } = useText('planning');
  const { call, me } = useCrmContext();
  const [monday, setMonday] = useState(() => mondayOf(start ?? new Date()));
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  // The week jumps to the appointment's when its day changes.
  const day = start ? isoDay(start) : null;
  // Same week, same Monday: a new date for it would read the week again (and
  // blank it) on every click in it.
  useEffect(() => {
    if (!day) return;
    const next = mondayOf(fromIsoDay(day));
    setMonday((current) => (isoDay(current) === isoDay(next) ? current : next));
  }, [day]);

  // Only the answer to the latest request lands.
  const lastRequest = useRef(0);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  useEffect(() => {
    if (!me) return;
    const request = lastRequest.current + 1;
    lastRequest.current = request;
    setData(null);
    setFailed(false);
    call(crmApi.getPlanning, { from: isoDay(monday), to: isoDay(addDays(monday, 7)) }).then(
      (next) => {
        if (request !== lastRequest.current) return;
        setData(next);
        onLoadedRef.current?.(next);
      },
      () => request === lastRequest.current && setFailed(true),
    );
  }, [call, me, monday]);

  const weekend = start && monday <= start && start < addDays(monday, 7) && [0, 6].includes(start.getDay());
  const days = useMemo(
    () => Array.from({ length: weekend ? 7 : 5 }, (_, index) => addDays(monday, index)),
    [monday, weekend],
  );
  const titleOf = (event) => {
    if (event.masked) return text('busy');
    return (event.category === 'interne' ? event.title : event.company?.name || event.title) || text('untitled');
  };
  const lookOf = (event) => ({
    look: appointmentLook(event, responseOf(event, me?.id, true)),
    invitation: isInvitation(event, me?.id),
    masked: Boolean(event.masked),
  });

  const now = new Date();
  const from = days[0];
  const to = addDays(days[days.length - 1], 1);
  const timed = (data?.events ?? []).filter((event) => !spansDays(event));
  const items = [
    ...timed
      .filter((event) => new Date(event.startsAt) >= from && new Date(event.startsAt) < to)
      .map((event) => ({
        id: event.id,
        title: titleOf(event),
        titleTime: clockLabel(new Date(event.startsAt)),
        subtitle: event.category === 'interne' ? '' : event.company?.city || event.location,
        start: new Date(event.startsAt),
        end: new Date(event.endsAt),
        category: event.category,
        past: new Date(event.endsAt) < now,
        google: event.source === 'google',
        ...lookOf(event),
      })),
    ...(data?.tasks ?? [])
      .filter((task) => task.dueTime)
      .map((task) => {
        const taskStart = new Date(`${task.dueOn}T${task.dueTime.slice(0, 5)}`);
        return {
          id: `task-${task.id}`,
          title: task.title,
          titleTime: clockLabel(taskStart),
          subtitle: task.company?.name ?? '',
          start: taskStart,
          end: new Date(taskStart.getTime() + (task.durationMin ?? 15) * 60000),
          category: 'interne',
          plain: true,
          past: Boolean(task.doneAt) || taskStart < now,
        };
      })
      .filter((item) => item.start >= from && item.start < to),
  ];
  const allDay = {};
  (data?.events ?? []).filter(spansDays).forEach((event) =>
    appointmentDays(event).forEach((one) => {
      allDay[one] = [
        ...(allDay[one] ?? []),
        { id: `appointment-${event.id}`, title: titleOf(event), category: event.category, google: event.source === 'google', appointment: true, ...lookOf(event) },
      ];
    }),
  );
  const draft = start ? { start, minutes, title } : null;
  // The hours of the week, stretched to hold the new appointment.
  const hours = gridHours([
    ...timed,
    ...(draft ? [{ startsAt: start, endsAt: new Date(start.getTime() + minutes * 60000) }] : []),
  ]);
  // The week of today, its weekend hidden or not.
  const showsToday = sameDay(monday, mondayOf(now));

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-3 px-4 pb-1 pt-4">
        <div className="flex items-center gap-1">
          <IconTooltip label={text('previous')}>
            <Button color="SECONDARY" size="ICON" className="h-9 w-9 border-gray-200" aria-label={text('previous')} onClick={() => setMonday(addDays(monday, -7))}>
              <ChevronLeft wh={16} color="#374151" />
            </Button>
          </IconTooltip>
          <IconTooltip label={text('next')}>
            <Button color="SECONDARY" size="ICON" className="h-9 w-9 border-gray-200" aria-label={text('next')} onClick={() => setMonday(addDays(monday, 7))}>
              <ChevronRight wh={16} color="#374151" />
            </Button>
          </IconTooltip>
        </div>
        <p className="text-sm font-semibold text-gray-900">{weekRange(monday)}</p>
        {!showsToday && (
          <Button color="SECONDARY" size="SM" onClick={() => setMonday(mondayOf(new Date()))}>
            {text('today')}
          </Button>
        )}
      </div>
      {failed && <p className="m-auto text-sm text-gray-600">{text('loadError')}</p>}
      {!failed && !data && (
        <div className="m-auto animate-spin">
          <Loading2 wh={24} color="#FF9600" />
        </div>
      )}
      {!failed && data && (
        <WeekGrid
          days={days}
          items={items}
          allDay={allDay}
          firstHour={hours.first}
          lastHour={hours.last}
          draft={draft}
          // A whole day has no slot to write: the appointment keeps its hours.
          onDraft={(picked) => !picked.allDay && onPick({ start: picked.start, minutes: picked.minutes })}
        />
      )}
    </div>
  );
};

export default WeekPicker;
